import React, { useRef, useState, useEffect, useContext } from "react";
import {
  Drawer,
  Row,
  Col,
  Typography,
  Button,
  Form,
  Input,
  message,
} from "antd";
import { CloseOutlined } from "@ant-design/icons";
import inputStyles from "../../../styles/Custom/Input.module.css";
import btnStyles from "../../../styles/Custom/Button.module.css";
import { getFacilityListApi } from "../../../services/facilities.services";
import { validateConfig } from "../../../config/validate.config";
import { userRoles } from "../../../helpers/utility";
import { addUserApi, updateUserApi } from "../../../services/auth.services";
import { listClinicalRolesApi } from "../../../services/clinicalForms.services";
import RoleForm from "./RoleForm";
import { useLocation } from "react-router-dom";
import { getCookie } from "../../../helpers/localStorage";
import { FacilityID, Role } from "../../../constants/defaultKeys";
import { UserContext } from "../../../context/UserContext";

const UsersForm = ({ isVisible, setIsVisible, editListData, refreshPage }) => {
  const { userDetails } = useContext(UserContext);
  const location = useLocation();
  const params = Object.fromEntries(new URLSearchParams(location?.search));
  const formRef = useRef(null);
  const [form] = Form.useForm();
  const [btnLoading, setBtnLoading] = useState(false);
  const [facilities, setFacilities] = useState([]);
  const [clinicalRoles, setClinicalRoles] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);

  const defaultRole = userDetails?.currRole || getCookie(Role);
  const canSeeClinicalRoles =
    defaultRole === "Organization_Owner" || defaultRole === "Organization_Admin";

  useEffect(() => {
    if (editListData) {
      const collapsedRoles = editListData?.roles?.map((r) =>
        r?.role === "Clinical_Practitioner" && r?.clinical_role_id
          ? { ...r, role: `clinical:${r.clinical_role_id}` }
          : r
      );
      form.setFieldsValue({
        ...editListData,
        ...(collapsedRoles ? { roles: collapsedRoles } : {}),
      });
    }
  }, [editListData]);

  useEffect(() => {
    getFacilityListApi()
      .then((res) => {
        setFacilities(res?.data);
      })
      .catch((err) => {
        console.log(err);
      });
  }, []);

  useEffect(() => {
    if (!canSeeClinicalRoles) return;
    listClinicalRolesApi({ is_active: true })
      .then((res) => {
        const list = Array.isArray(res?.data)
          ? res.data
          : res?.data?.results || res?.data?.data || [];
        setClinicalRoles(list.filter((r) => r.is_active !== false));
      })
      .catch(() => {
        /* non-fatal — dropdown just won't show clinical roles */
      });
  }, [canSeeClinicalRoles]);

  // Merge any clinical roles already assigned to the editing user into the
  // dropdown list (even if deactivated), so the Select can label them by name.
  useEffect(() => {
    if (!editListData?.roles) return;
    const extras = editListData.roles
      .filter((r) => r?.role === "Clinical_Practitioner" && r?.clinical_role_id)
      .map((r) => ({
        id: r.clinical_role_id,
        name: r.clinical_role_name || `Clinical Role ${r.clinical_role_id}`,
        is_active: true,
      }));
    if (!extras.length) return;
    setClinicalRoles((prev) => {
      const ids = new Set(prev.map((x) => Number(x.id)));
      const additions = extras.filter((x) => !ids.has(Number(x.id)));
      return additions.length ? [...prev, ...additions] : prev;
    });
  }, [editListData]);

  const expandClinicalRole = (role) => {
    if (typeof role?.role !== "string" || !role.role.startsWith("clinical:")) {
      if (role?.role !== "Clinical_Practitioner") {
        const {
          clinical_role_id,
          clinical_role_name,
          clinical_role,
          ...rest
        } = role || {};
        return rest;
      }
      return role;
    }
    const id = Number(role.role.split(":")[1]);
    return {
      ...role,
      role: "Clinical_Practitioner",
      clinical_role_id: id,
    };
  };

  const onFinish = (values) => {
    const normalizedRoles = values?.roles?.map(expandClinicalRole);

    const facilityScopedRoles = new Set([
      "Doctor",
      "Nurse",
      "Physiotherapist",
      "Speech_Swallow_Therapist",
      "Respiratory_Therapist",
      "Nutritionist",
      "Receptionist",
      "Facility_Admin",
      "Psychologist",
      "Pharmacist",
      "Clinical_Practitioner",
    ]);

    const rolesPayload = normalizedRoles?.map((role) => {
      const existingRole = editListData?.roles?.find(
        (editItem) => editItem?.org_user_id === role?.org_user_id
      );
      const isFacilityScoped = facilityScopedRoles.has(role?.role);
      let facility = role?.facility ?? existingRole?.facility;

      if (
        isFacilityScoped &&
        (facility === undefined || facility === null || facility === "")
      ) {
        facility = getCookie(FacilityID) || userDetails?.currFacility;
      }

      return isFacilityScoped ? { ...role, facility } : role;
    });

    const formSubmitData = { ...values, roles: rolesPayload };

    setBtnLoading(true);
    if (editListData) {
      updateUserApi({ ...formSubmitData, user_id: editListData?.user_id })
        .then((res) => {
          message.success(res?.data?.message);
          setIsVisible(false);
          setBtnLoading(false);
          refreshPage();
        })
        .catch((err) => {
          message.error(err?.response?.data?.message);
          setBtnLoading(false);
        });
    } else {
      addUserApi(formSubmitData)
        .then((res) => {
          message.success(res?.data?.message);
          setIsVisible(false);
          setBtnLoading(false);
          refreshPage();
        })
        .catch((err) => {
          message.error(err?.response?.data?.message);
          setBtnLoading(false);
        });
    }
  };

  const onClose = () => {
    setIsVisible(false);
  };

  return (
    <Drawer
      open={isVisible}
      width={480}
      title={
        <Row>
          <Col span={12} className="flex-start">
            <Typography
              style={{
                marginLeft: "12px",
                fontWeight: "400",
                fontSize: "20px",
              }}
            >
              {editListData ? "Edit User" : "Add User"}
            </Typography>
          </Col>
          <Col span={12} className="flex-end">
            <CloseOutlined onClick={onClose} />
          </Col>
        </Row>
      }
      footer={
        <div style={{ textAlign: "right" }}>
          <Form.Item>
            <Button
              loading={btnLoading}
              className={btnStyles.lgBtn}
              type="primary"
              htmlType="submit"
              onClick={() => {
                if (formRef.current) {
                  formRef.current.submit();
                }
              }}
            >
              Submit
            </Button>
          </Form.Item>
        </div>
      }
      onClose={onClose}
      closeIcon={null}
    >
      <Form
        onFinish={onFinish}
        layout="vertical"
        form={form}
        ref={formRef}
        initialValues={{
          roles: [{}],
        }}
      >
        <Form.Item
          label="First Name"
          name="first_name"
          rules={validateConfig.isRequired("First Name")}
        >
          <Input
            className={inputStyles.mdInput}
            style={{ width: "300px" }}
            placeholder="Enter first name"
          />
        </Form.Item>
        <Form.Item
          label="Last Name"
          name="last_name"
          rules={validateConfig.isRequired("Last Name")}
        >
          <Input
            className={inputStyles.mdInput}
            style={{ width: "300px" }}
            placeholder="Enter last name"
          />
        </Form.Item>
        <Form.Item
          label="Contact Number"
          name="mobile"
          rules={[
            { required: true, message: "Please enter mobile number" },
            {
              pattern: /^[0-9]{10}$/,
              message: "Please enter a valid 10-digit phone number",
            },
          ]}
        >
          <Input
            style={{ width: "300px" }}
            maxLength="10"
            placeholder="Mobile number"
            addonBefore={"+91"}
            className={inputStyles.mdAddonInput}
          />
        </Form.Item>
        <Form.Item
          label="Email"
          name="email"
          rules={[
            { type: "email", message: "Please enter a valid email" },
            { required: true, message: "Please enter email" },
          ]}
        >
          <Input
            placeholder="Enter email"
            className={inputStyles.mdInput}
            style={{ width: "300px" }}
          />
        </Form.Item>
        <RoleForm
          userRoles={userRoles}
          clinicalRoles={clinicalRoles}
          editListData={editListData}
          selectedRoles={selectedRoles}
          setSelectedRoles={setSelectedRoles}
          form={form}
          facilities={facilities}
          params={params}
          userDetails={userDetails}
        />
      </Form>
    </Drawer>
  );
};

export default UsersForm;
